import React, { useState, useRef, useEffect, useCallback } from 'react';
import { exportCSV, exportXLSX, exportPDF, exportICS } from '../utils/exportUtils';
import { useSchedule } from '../context/ScheduleContext';

const btnBase: React.CSSProperties = {
  padding: '4px 10px',
  fontSize: 12,
  border: '1px solid #cbd5e1',
  borderRadius: 4,
  background: '#fff',
  cursor: 'pointer',
  color: '#1e293b',
};

const menuItemStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  padding: '7px 14px',
  fontSize: 12,
  textAlign: 'left',
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  color: '#1e293b',
  whiteSpace: 'nowrap',
};

interface ExportOption {
  label: string;
  description: string;
}

const OPTIONS: ExportOption[] = [
  { label: 'CSV',   description: 'Comma-separated values (.csv)' },
  { label: 'Excel', description: 'Excel workbook (.xlsx)' },
  { label: 'PDF',   description: 'PDF document (.pdf)' },
  { label: 'ICS',   description: 'Calendar file (.ics)' },
];

export function ExportMenu() {
  const { events, viewRange, config } = useSchedule();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [hovered, setHovered] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const exportCfg = config.toolbarButtons?.export;
  const label = exportCfg?.label ?? 'Export';

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleExport = useCallback(
    async (format: string) => {
      setOpen(false);
      setBusy(true);
      try {
        const resources = config.resources ?? [];
        const tz = config.timezone;
        switch (format) {
          case 'CSV':   exportCSV(events, viewRange, tz, resources); break;
          case 'Excel': await exportXLSX(events, viewRange, tz, resources); break;
          case 'PDF':   await exportPDF(events, viewRange, tz, resources); break;
          case 'ICS':   exportICS(events, viewRange); break;
        }
      } finally {
        setBusy(false);
      }
    },
    [events, viewRange, config],
  );

  return (
    <div ref={menuRef} style={{ position: 'relative' }}>
      <button
        style={{ ...btnBase, opacity: busy ? 0.6 : 1 }}
        onClick={() => setOpen((v) => !v)}
        disabled={busy}
        aria-haspopup="true"
        aria-expanded={open}
        title={label}
        aria-label={label}
      >
        {busy ? 'Exporting…' : `${label} ▾`}
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            right: 0,
            zIndex: 1000,
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: 6,
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
            minWidth: 200,
            overflow: 'hidden',
          }}
        >
          {OPTIONS.map(({ label, description }) => (
            <button
              key={label}
              role="menuitem"
              style={{
                ...menuItemStyle,
                background: hovered === label ? '#f1f5f9' : 'none',
              }}
              onMouseEnter={() => setHovered(label)}
              onMouseLeave={() => setHovered(null)}
              onClick={() => handleExport(label)}
            >
              <span style={{ fontWeight: 500 }}>{label}</span>
              <span style={{ marginLeft: 8, color: '#64748b', fontSize: 11 }}>{description}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
